import Pill from '../components/Pill.jsx';
import UpdatedAt from '../components/UpdatedAt.jsx';
import UsageBar from '../components/UsageBar.jsx';
import { useLiveData } from '../lib/useLiveData.js';
import { useSession, ROLES } from '../lib/session.jsx';
import { prettyDate } from '../lib/dateRange.js';

/* Allocated minutes — what the plan allows and what has been used of it.

   A bundle is a number of minutes across a set of destinations, not a number
   per destination: "2,000 minutes to the UK and Europe" is one pool that a
   call to either draws down. So an extension shows one bar per bundle it
   holds, labelled with the coverage it was sold as — UK, UK/EU, RO/EU/UK/US —
   and anything outside those is totalled separately as "not included".

   The figures come from the portal's own metering, not the PBX on demand:
   walking a month of CDR takes seconds and this page has to open quickly. A
   cron job meters the calls; this reads the totals. That is also why the page
   carries an "as at" — the numbers are minutes old by construction, and a
   figure with no timestamp is one people over-trust. */
export default function Minutes() {
  const { role } = useSession();
  const { status, data, error, updatedAt, refresh } = useLiveData('/minutes', { intervalMs: 300000 });

  const mine = role === ROLES.EXTENSION_USER;

  if (status === 'loading') {
    return <div className="card"><div className="empty"><p className="empty__body">Loading your minutes…</p></div></div>;
  }

  if (status === 'error') {
    return (
      <div className="card">
        <div className="empty">
          <div className="empty__title">We couldn't load your minutes</div>
          <p className="empty__body">{error?.message}</p>
        </div>
      </div>
    );
  }

  const { period, items = [], tenant, unconfigured = 0 } = data ?? {};

  if (!period || items.length === 0) {
    return (
      <div className="card">
        <div className="empty">
          <div className="empty__title">No allowance information</div>
          <p className="empty__body">
            We don't have allocated-minute bundles recorded for your extensions yet.
            Please contact support if you expect to see them here.
          </p>
        </div>
      </div>
    );
  }

  const anyNotIncluded = items.some((r) => r.not_included.minutes > 0);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, marginBottom: 14 }}>
        <div style={{ fontSize: 13, color: 'var(--ink-soft)' }}>
          Billing period {prettyDate(period.start)} – {prettyDate(period.end)}
        </div>
        <UpdatedAt at={updatedAt} onRefresh={refresh} stale={Boolean(error)} />
      </div>

      {/* Account totals, one card per distinct coverage. Grouped rather than
          summed flat: "62,000 UK minutes" and "4,000 EU minutes" are two facts,
          and adding them would describe nothing. An extension user gets none of
          this — it is their colleagues' calls. */}
      {tenant && tenant.length > 0 && (
        <div className="grid-2" style={{ marginBottom: 18 }}>
          {tenant.map((t) => <TotalCard key={t.key} measure={t} />)}
        </div>
      )}

      <div className="card">
        <div className="table-wrap">
          <table className="table table--sized">
            {/* Same reasoning as the extensions list: proportional widths so
                the bars get the room and nothing inherits the slack by accident. */}
            <colgroup>
              <col style={{ width: '9%' }} />
              <col style={{ width: anyNotIncluded ? '25%' : '29%' }} />
              <col style={{ width: anyNotIncluded ? '38%' : '44%' }} />
              {anyNotIncluded && <col style={{ width: '10%' }} />}
              <col style={{ width: '18%' }} />
            </colgroup>
            <thead>
              <tr>
                <th>Ext.</th>
                <th>Name</th>
                <th>Allocated minutes</th>
                {anyNotIncluded && <th>Not included</th>}
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr key={row.extension}>
                  <td className="td--mono" style={{ fontWeight: 500, color: 'var(--signal-deep)', verticalAlign: 'top' }}>
                    {row.extension}
                  </td>
                  <td
                    className="td--truncate"
                    style={{ fontWeight: 600, verticalAlign: 'top' }}
                    title={mine ? undefined : row.name}
                  >
                    {mine ? 'You' : row.name}
                  </td>
                  <td style={{ paddingRight: 24 }}>
                    {row.bundles.length === 0 ? (
                      <span className="td--soft" style={{ fontSize: 13 }}>No bundle on this plan</span>
                    ) : (
                      row.bundles.map((b, i) => (
                        <div key={b.key} style={{ marginTop: i === 0 ? 0 : 12 }}>
                          <UsageBar measure={b} coverage={b.coverage} title={b.coverage_description} />
                        </div>
                      ))
                    )}
                  </td>
                  {anyNotIncluded && (
                    /* Destinations this extension's own bundles don't cover —
                       a US call on a UK/EU plan, premium rate, freephone, 999.
                       Shown so the figures reconcile against call history. */
                    <td className="td--soft" style={{ fontSize: 13, verticalAlign: 'top' }}>
                      {row.not_included.minutes > 0 ? `${row.not_included.minutes} min` : '—'}
                    </td>
                  )}
                  <td style={{ verticalAlign: 'top' }}><StatePill state={row.state} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {unconfigured > 0 && (
        <p className="footnote">
          {unconfigured === 1
            ? '1 extension has no allowance recorded on its plan, so nothing is counted against it.'
            : `${unconfigured} extensions have no allowance recorded on their plan, so nothing is counted against them.`}
        </p>
      )}

      <p className="footnote">
        Outbound calls only, counted as talk time — ringing isn't counted, and neither are
        calls between your own extensions. Calls to destinations outside your bundle, along
        with freephone, premium-rate and emergency calls, are shown but don't use your
        allocated minutes.
      </p>
    </div>
  );
}

function TotalCard({ measure }) {
  if (!measure?.allowance?.configured) return null;

  return (
    <div className="card stat">
      <div className="stat__label" title={measure.coverage_description}>
        {measure.coverage} minutes — whole account
      </div>
      <div className="stat__value" style={{ color: TONE[measure.state] }}>
        {measure.percent !== null ? `${measure.percent}%` : '—'}
      </div>
      <div style={{ marginTop: 10 }}>
        <UsageBar measure={measure} showLabel={false} />
      </div>
      <div className="stat__sub" style={{ marginTop: 8 }}>
        {measure.minutes} of {measure.allowance.minutes} min used
        {measure.contributing_extensions > 0
          ? ` · pooled across ${measure.contributing_extensions} extensions`
          : ''}
      </div>
    </div>
  );
}

const TONE = {
  ok: undefined,
  warning: 'var(--amber)',
  over: 'var(--miss)',
  unlimited: undefined,
  unset: undefined,
};

const STATE_LABEL = {
  ok: 'Within allowance',
  warning: 'Approaching limit',
  over: 'Allowance used',
  unlimited: 'Unlimited',
  unset: 'Not set',
};

const STATE_TONE = {
  ok: 'online',
  warning: 'ringing',
  over: 'missed',
  unlimited: 'online',
  unset: 'offline',
};

function StatePill({ state }) {
  return <Pill tone={STATE_TONE[state] ?? 'offline'}>{STATE_LABEL[state] ?? state}</Pill>;
}
